import { createI18n } from 'vue-i18n'

import { en } from '@/locales/en'
import { de } from '@/locales/de'
import { it } from '@/locales/it'
import { hr } from '@/locales/hr'
import { fr } from '@/locales/fr'
import { es } from '@/locales/es'
import { pt } from '@/locales/pt'
import axios from 'axios'

const messages = {
  en,
  de,
  it,
  hr,
  fr,
  es,
  pt,
}

// Endonyms, so a language is recognisable whatever the UI is set to. Shared by every language menu
// (public profile, PDF preview, the multilingual editor's "+ language" menu).
export const LANGUAGE_LABELS: Record<string, string> = {
  de: 'Deutsch',
  en: 'English',
  it: 'Italiano',
  hr: 'Hrvatski',
  fr: 'Français',
  es: 'Español',
  pt: 'Português',
}

// Anonymous viewers (public profile, shared certificate links) have no profile to read a language
// from, so start from the browser instead of always landing on the env default. Logged-in users
// still get overridden by their profile language in the app bar's mounted().
// Derived from `messages` rather than a locale list of its own: importing MULTILINGUAL_LOCALES from
// utils/localizeString would be circular (that module imports getLocale from here).
function detectLocale () {
  const browserLang = navigator.language?.split('-')[0] // 'it-IT' → 'it'
  return Object.keys(messages).includes(browserLang)
    ? browserLang
    : process.env.VUE_APP_I18N_LOCALE || 'en'
}

const instance = createI18n({
  locale: detectLocale(),
  fallbackLocale: process.env.VUE_APP_I18N_FALLBACK_LOCALE || 'en',
  messages,
  globalInjection: true,
  allowComposition: true,
})

export function setLocale(lang: string) {
  if (!(lang in messages)) return
  instance.global.locale.value = lang

  axios.defaults.headers.common['Accept-Language'] = getLocale()
}

// `instance.global.locale` is only a ref because @intlify/unplugin-vue-i18n defaults to
// compositionOnly: true (which defines __VUE_I18N_LEGACY_API__ = false). Setting compositionOnly:
// false or legacy: true would make this return undefined — silently forcing every multilingual
// field (certificate titles, standards, checklist questions) to German, since getLocalizedString()
// resolves through here.
export function getLocale() {
  return instance.global.locale.value
}

export default instance
